import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { toastError } from "@/lib/errorToast";
import type { PlanningBlock, PlanningWeek, PlanningRules, BlockType } from "@/types/planning";
import { DEFAULT_RULES, blocksOverlap, hydrateBlocksStable } from "@/types/planning";

function generateId() {
  return crypto.randomUUID();
}

function serializeWeek(blocks: PlanningBlock[], rules: PlanningRules): PlanningWeek {
  // Exceptional blocks are NOT saved into the permanent template
  const permanentBlocks = blocks.filter((b) => b.persistence !== "exceptional");
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    week_template: permanentBlocks.map(({ id, ...rest }) => rest),
    rules,
  };
}

function deserializeWeek(data: PlanningWeek | null): { blocks: PlanningBlock[]; rules: PlanningRules } {
  if (!data) return { blocks: [], rules: { ...DEFAULT_RULES } };
  const blocks = hydrateBlocksStable(data.week_template || []);
  return { blocks, rules: { ...DEFAULT_RULES, ...data.rules } };
}

function hydrate(blocks: Omit<PlanningBlock, "id">[]): PlanningBlock[] {
  return hydrateBlocksStable(blocks);
}

// ── Undo/Redo ──

const MAX_HISTORY = 40;

interface Snapshot {
  blocks: PlanningBlock[];
  rules: PlanningRules;
}

export function usePlanningWeek() {
  const { user } = useAuth();
  const [blocks, setBlocks] = useState<PlanningBlock[]>([]);
  const [rules, setRulesState] = useState<PlanningRules>({ ...DEFAULT_RULES });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const savedRef = useRef<string>("");
  const blocksRef = useRef<PlanningBlock[]>([]);
  const rulesRef = useRef<PlanningRules>({ ...DEFAULT_RULES });

  // History
  const pastRef = useRef<Snapshot[]>([]);
  const futureRef = useRef<Snapshot[]>([]);
  const [historyTick, setHistoryTick] = useState(0);

  const snapshot = useCallback((): Snapshot => ({ blocks: [...blocks], rules: { ...rules } }), [blocks, rules]);

  const pushHistory = useCallback(() => {
    const snap: Snapshot = { blocks: [...blocks], rules: { ...rules } };
    pastRef.current = [...pastRef.current.slice(-(MAX_HISTORY - 1)), snap];
    futureRef.current = [];
    setHistoryTick((t) => t + 1);
  }, [blocks, rules]);

  const undo = useCallback(() => {
    if (pastRef.current.length === 0) return;
    const prev = pastRef.current[pastRef.current.length - 1];
    pastRef.current = pastRef.current.slice(0, -1);
    futureRef.current = [...futureRef.current, { blocks: [...blocks], rules: { ...rules } }];
    setBlocks(prev.blocks);
    setRulesState(prev.rules);
    setHistoryTick((t) => t + 1);
  }, [blocks, rules]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    const next = futureRef.current[futureRef.current.length - 1];
    futureRef.current = futureRef.current.slice(0, -1);
    pastRef.current = [...pastRef.current, { blocks: [...blocks], rules: { ...rules } }];
    setBlocks(next.blocks);
    setRulesState(next.rules);
    setHistoryTick((t) => t + 1);
  }, [blocks, rules]);

  const canUndo = pastRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;

  // Load
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const { data, error } = await supabase
          .from("user_intent_profiles")
          .select("planning_week")
          .eq("user_id", user.id)
          .maybeSingle();
        if (error) {
          console.error("[usePlanningWeek] load error", error);
          setLoading(false);
          return;
        }
        const pw = (data as any)?.planning_week as PlanningWeek | null;
        const { blocks: b, rules: r } = deserializeWeek(pw);
        console.log("[usePlanningWeek] Loaded blocks:", b.length, "sport:", b.filter(x => x.type === "sport").length);
        setBlocks(b);
        blocksRef.current = b;
        setRulesState(r);
        rulesRef.current = r;
        savedRef.current = JSON.stringify(serializeWeek(b, r));
      } catch (err) {
        console.error("[usePlanningWeek] unexpected load error", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  // Keep refs in sync
  useEffect(() => {
    blocksRef.current = blocks;
    rulesRef.current = rules;
  }, [blocks, rules]);

  // Track dirty
  useEffect(() => {
    const current = JSON.stringify(serializeWeek(blocks, rules));
    setDirty(current !== savedRef.current);
  }, [blocks, rules]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo]);

  const save = useCallback(async () => {
    if (!user) return;
    setSaving(true);
    const payload = serializeWeek(blocks, rules);
    const { error } = await supabase
      .from("user_intent_profiles")
      .update({ planning_week: payload } as any)
      .eq("user_id", user.id);
    setSaving(false);
    if (error) {
      toastError("Erreur lors de la sauvegarde du planning.", "Sauvegarder le planning", error.message);
      console.error(error);
    } else {
      savedRef.current = JSON.stringify(payload);
      setDirty(false);
      toast.success("Planning enregistré !");
    }
  }, [user, blocks, rules]);

  const revert = useCallback(() => {
    pushHistory();
    const { blocks: b, rules: r } = deserializeWeek(JSON.parse(savedRef.current || "null"));
    setBlocks(b);
    setRulesState(r);
    setDirty(false);
  }, [pushHistory]);

  // ── Collision-aware block operations ──

  const findCollisions = useCallback((newBlock: PlanningBlock, excludeId?: string) => {
    return blocks.filter((b) => {
      if (b.id === excludeId) return false;
      return blocksOverlap(newBlock, b);
    });
  }, [blocks]);

  const addBlock = useCallback((block: Omit<PlanningBlock, "id">) => {
    pushHistory();
    const newBlock: PlanningBlock = { ...block, id: generateId() };
    const collisions = findCollisions(newBlock);
    if (collisions.length > 0) {
      toast.warning("Chevauchement détecté — bloc ajouté malgré tout.");
    }
    setBlocks((prev) => [...prev, newBlock]);
    return newBlock;
  }, [pushHistory, findCollisions]);

  /** Add a block and immediately persist to DB */
  const addBlockAndSave = useCallback(async (block: Omit<PlanningBlock, "id">) => {
    if (!user) return;
    pushHistory();
    const newBlock: PlanningBlock = { ...block, id: generateId() };
    const newBlocks = [...blocksRef.current, newBlock];
    setBlocks(newBlocks);
    blocksRef.current = newBlocks;
    // Save immediately with the latest state
    setSaving(true);
    const currentRules = rulesRef.current;
    const payload = serializeWeek(newBlocks, currentRules);
    const { error } = await supabase
      .from("user_intent_profiles")
      .update({ planning_week: payload } as any)
      .eq("user_id", user.id);
    setSaving(false);
    if (error) {
      toastError("Erreur lors de la sauvegarde.", "Ajouter un bloc au planning", error.message);
      console.error(error);
    } else {
      savedRef.current = JSON.stringify(payload);
      setDirty(false);
    }
    return newBlock;
  }, [user, pushHistory]);

  const addBlocks = useCallback((newBlocks: Omit<PlanningBlock, "id">[]) => {
    pushHistory();
    setBlocks((prev) => [...prev, ...hydrate(newBlocks)]);
  }, [pushHistory]);

  const updateBlock = useCallback((id: string, patch: Partial<PlanningBlock>) => {
    pushHistory();
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }, [pushHistory]);

  const removeBlock = useCallback((id: string) => {
    pushHistory();
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  }, [pushHistory]);

  const replaceAllBlocks = useCallback((newBlocks: Omit<PlanningBlock, "id">[]) => {
    pushHistory();
    setBlocks(hydrate(newBlocks));
  }, [pushHistory]);

  const setRules = useCallback((r: PlanningRules) => {
    pushHistory();
    setRulesState(r);
  }, [pushHistory]);

  return {
    blocks, rules, setRules, loading, saving, dirty,
    save, revert,
    addBlock, addBlockAndSave, addBlocks, updateBlock, removeBlock, replaceAllBlocks,
    undo, redo, canUndo, canRedo,
  };
}
